import { AlertTriangle, Building2, Folder, LayoutGrid, Laptop } from 'lucide-react';

import { MigrationGroupsListData, MigrationGroupRollup } from '../api/migrationGroups';
import { MetricCard, MetricRow } from './abPrimitives';

// The groups workspace metric row.
//
// FD-467 — this was the FD-280 gapless "matrix": cells separated by 1px strokes
// on a shared ground, argued for on the grounds that "a console metric is a
// fact, and a floating card reads as a marketing tile". FD-ADR-043 supersedes
// ADR-031 §2 on visuals and the AirBridge board uses bordered cards — but the
// half of that argument that was about SUBSTANCE survives intact: these are
// still bordered and still shadow-free, and `MetricCard` deliberately has no
// delta slot, so none of them can grow a "↑ 12%" nobody can compute (D4).
//
// Two real defects went with the restyle. The cells rendered `font-mono`, which
// FD-ADR-043 D2 retired with no replacement (the numerals now carry `NUMERIC`
// via MetricCard), and the warning cell hardcoded `#FFFBEB` / `#D97706` — a
// retired amber pair that predates the three-value STATUS vocabulary.
//
// WHY `unknownGroup` STILL GETS A CARD AND "No group" NO LONGER DOES (FD-474)
//
// FD-ADR-030 is explicit that a `MigrationGroupId` matching no group row is a
// DIFFERENT operator question from "has no group" — the first means a delete/
// assign race or the §7 partition orphaning, the second is the ordinary state of
// a campaign nobody filed. That distinction is unchanged and is exactly why the
// two were never folded together.
//
// What changed is the audience for the second half. The create-campaign wizard
// always assigns a group — `groupChoice` defaults to creating one and step 3
// cannot be completed without either a new name or an existing group — so no
// campaign the CONSOLE creates is ungrouped, and a permanent zero on the
// landing page is a card that teaches an operator to stop reading the row.
//
// IT IS NOT STRUCTURALLY IMPOSSIBLE, and that is the part worth keeping
// straight. `POST /v1/migrations` still takes `migrationGroupId` as OPTIONAL
// (FD-ADR-030 §1 defines `""` as no group and calls groups optional), the
// `GroupPicker` still offers "No group" on a campaign that has one, and the
// smoke suite creates campaigns before assigning them. So the state remains
// reachable — it is just no longer worth a permanent tile. The way to see it is
// the migrations list's `?group=none` filter, which the empty-state message
// below still links to.
//
// `unknownGroup` keeps its card because it is a REAL DATA PROBLEM rather than an
// ordinary state: it appears ONLY when the count is non-zero, and it is the one
// card that carries a warning tone.

function total(r: MigrationGroupRollup | undefined, key: 'migrationCount' | 'deviceCount') {
  return (r?.[key] ?? 0).toLocaleString();
}

export default function GroupMetricsMatrix({ data }: { data: MigrationGroupsListData }) {
  const distinctDestinations = new Set(
    (data.totals?.destinations ?? []).map((d) => d.tenantId),
  ).size;

  const unknownCount = data.unknownGroup?.migrationCount ?? 0;

  return (
    <MetricRow>
      <MetricCard
        label="Groups"
        value={data.groups.length.toLocaleString()}
        icon={<LayoutGrid size={18} strokeWidth={1.5} />}
      />
      <MetricCard
        label="Campaigns"
        value={total(data.totals, 'migrationCount')}
        caption="Across all groups"
        icon={<Folder size={18} strokeWidth={1.5} />}
      />
      <MetricCard
        label="Devices assigned"
        value={total(data.totals, 'deviceCount')}
        // Assignment, not progress — DeviceCount moves only on add/remove and
        // never on wave completion.
        caption="Assigned, not migrated"
        icon={<Laptop size={18} strokeWidth={1.5} />}
      />
      <MetricCard
        label="Destination tenants"
        value={distinctDestinations.toLocaleString()}
        caption={
          distinctDestinations === 1 ? 'Across all campaigns' : 'Distinct, across all campaigns'
        }
        icon={<Building2 size={18} strokeWidth={1.5} />}
      />
      {unknownCount > 0 && (
        <MetricCard
          label="Unresolvable group"
          value={unknownCount.toLocaleString()}
          caption="References a group that no longer exists"
          tone="warning"
          icon={<AlertTriangle size={18} strokeWidth={1.5} />}
        />
      )}
    </MetricRow>
  );
}
